import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import '../../styles/Auth.css';

/**
 * Página para restablecer contraseña con token
 */
function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [formData, setFormData] = useState({
    password: '',
    confirmarPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [validando, setValidando] = useState(true);
  const [tokenValido, setTokenValido] = useState(false);
  const [email, setEmail] = useState('');
  const [mensaje, setMensaje] = useState({ tipo: '', texto: '' });
  const [exito, setExito] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    password: false,
    confirmar: false
  });

  const apiUrl = process.env.REACT_APP_API_URL || '/api';

  // Validar token al cargar
  useEffect(() => {
    const validarToken = async () => {
      if (!token) {
        setValidando(false);
        setMensaje({ tipo: 'error', texto: 'Enlace inválido. No se encontró el token.' });
        return;
      }

      try {
        const res = await fetch(`${apiUrl}/auth/validar-token/${token}`);
        const data = await res.json();
        if (data.success) {
          setTokenValido(true);
          setEmail(data.email);
        } else {
          setMensaje({ tipo: 'error', texto: data.message || 'El enlace ha expirado o no es válido' });
        }
      } catch (error) {
        setMensaje({
          tipo: 'error',
          texto: 'El enlace ha expirado o no es válido'
        });
      } finally {
        setValidando(false);
      }
    };

    validarToken();
  }, [token, apiUrl]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setMensaje({ tipo: '', texto: '' });
  };

  const toggleShowPassword = (field) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMensaje({ tipo: '', texto: '' });

    // Validaciones
    if (!formData.password || !formData.confirmarPassword) {
      setMensaje({ tipo: 'error', texto: 'Todos los campos son obligatorios' });
      return;
    }

    if (formData.password.length < 6) {
      setMensaje({ tipo: 'error', texto: 'La contraseña debe tener al menos 6 caracteres' });
      return;
    }

    if (formData.password !== formData.confirmarPassword) {
      setMensaje({ tipo: 'error', texto: 'Las contraseñas no coinciden' });
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(`${apiUrl}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          password: formData.password,
          confirmarPassword: formData.confirmarPassword
        })
      });
      const data = await res.json();

      if (data.success) {
        setExito(true);
        setMensaje({
          tipo: 'success',
          texto: data.message
        });

        // Redirigir al login después de 3 segundos
        setTimeout(() => {
          navigate('/');
        }, 3000);
      } else {
        setMensaje({
          tipo: 'error',
          texto: data.message || 'Error al restablecer la contraseña'
        });
      }
    } catch (error) {
      setMensaje({
        tipo: 'error',
        texto: 'Error al restablecer la contraseña'
      });
    } finally {
      setLoading(false);
    }
  };

  // Mostrar loading mientras valida
  if (validando) {
    return (
      <div className="auth-wrapper">
        <div className="auth-inner">
          <h3>AcademiK</h3>
          <p className="login-subtitle">Validando enlace...</p>
        </div>
      </div>
    );
  }

  // Token inválido o expirado
  if (!tokenValido && !exito) {
    return (
      <div className="auth-wrapper">
        <div className="auth-inner">
          <h3>AcademiK</h3>
          <p className="login-subtitle">Restablecer Contraseña</p>

          <div className="auth-error" style={{ marginTop: '20px' }}>
            {mensaje.texto}
          </div>

          <button
            className="btn-auth"
            onClick={() => navigate('/')}
            style={{ marginTop: '20px' }}
          >
            Volver al inicio
          </button>
        </div>
      </div>
    );
  }

  // Éxito
  if (exito) {
    return (
      <div className="auth-wrapper">
        <div className="auth-inner">
          <h3>AcademiK</h3>
          <p className="login-subtitle">Contraseña Restablecida</p>

          <div className="auth-success" style={{ marginTop: '20px' }}>
            {mensaje.texto}
          </div>

          <p style={{ color: '#6b7280', marginTop: '15px', fontSize: '14px' }}>
            Serás redirigido al inicio de sesión...
          </p>
        </div>
      </div>
    );
  }

  // Formulario de nueva contraseña
  return (
    <div className="auth-wrapper">
      <div className="auth-inner">
        <form onSubmit={handleSubmit} autoComplete="off">
          <h3>AcademiK</h3>
          <p className="login-subtitle">Nueva Contraseña</p>

          <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '20px' }}>
            Ingresa tu nueva contraseña para: <strong>{email}</strong>
          </p>

          <div className="form-group">
            <label>Nueva Contraseña</label>
            <div className="password-input-container">
              <input
                name="password"
                type={showPasswords.password ? 'text' : 'password'}
                className="form-control"
                onChange={handleChange}
                value={formData.password}
                required
                autoComplete="new-password"
                placeholder="Ingresa tu nueva contraseña"
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => toggleShowPassword('password')}
              >
                {showPasswords.password ? '🙈' : '👁️'}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label>Confirmar Contraseña</label>
            <div className="password-input-container">
              <input
                name="confirmarPassword"
                type={showPasswords.confirmar ? 'text' : 'password'}
                className="form-control"
                onChange={handleChange}
                value={formData.confirmarPassword}
                required
                autoComplete="new-password"
                placeholder="Confirma tu nueva contraseña"
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => toggleShowPassword('confirmar')}
              >
                {showPasswords.confirmar ? '🙈' : '👁️'}
              </button>
            </div>
          </div>

          {mensaje.texto && (
            <div className={mensaje.tipo === 'error' ? 'auth-error' : 'auth-success'}>
              {mensaje.texto}
            </div>
          )}

          <button type="submit" className="btn-auth" disabled={loading}>
            {loading ? 'Guardando...' : 'Guardar Nueva Contraseña'}
          </button>

          <p style={{ textAlign: 'center', marginTop: '15px' }}>
            <a href="/" style={{ color: '#667eea', textDecoration: 'none' }}>
              Volver al inicio de sesión
            </a>
          </p>
        </form>
      </div>
    </div>
  );
}

export default ResetPassword;
